import { API_ENDPOINTS } from '@/constants/api';
import { EntityForm } from '@/shared/components/EntityForm';
import { PageLayout } from '@/shared/components/PageLayout';
import { USER_PERMISSIONS } from '@/shared/types/permissions';
import type { RootState } from '@/store';
import {
  CBadge,
  CFormCheck,
  CFormInput,
  CNav,
  CNavItem,
  CNavLink,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableRow,
} from '@coreui/react';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { rolesApi } from '../api';
import { useRoleForm } from '../hooks/useRoleForm';
import type { PermissionOption, RoleData, RoleOption } from '../types';

export const RoleForm = () => {
  const navigate = useNavigate();
  const { entity: role, error, handleSubmit, isEdit } = useRoleForm();
  const [data, setData] = useState<Omit<RoleData, 'level'> & { level: string }>(
    role
      ? { name: role.name, level: String(role.level), permissions: role.permissions }
      : { name: '', level: '0', permissions: [] },
  );
  const [activeTab, setActiveTab] = useState('Customers');
  const [autoPermissions, setAutoPermissions] = useState<string[]>([]);
  const [previousRole, setPreviousRole] = useState(role);
  const [catalog, setCatalog] = useState<PermissionOption[]>([]);
  const [roles, setRoles] = useState<RoleOption[]>([]);
  const [catalogError, setCatalogError] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [validation, setValidation] = useState('');
  const [saving, setSaving] = useState(false);
  const currentUser = useSelector((state: RootState) => state.users.currentUser);

  if (role !== previousRole) {
    setPreviousRole(role);
    setAutoPermissions([]);
    setData(
      role
        ? { name: role.name, level: String(role.level), permissions: role.permissions }
        : { name: '', level: '0', permissions: [] },
    );
  }

  useEffect(() => {
    let active = true;
    void Promise.all([rolesApi.getPermissions(), rolesApi.getOptions()])
      .then(([options, roleOptions]) => {
        if (active) {
          setCatalog(options);
          setRoles(roleOptions);
          setLoaded(true);
        }
      })
      .catch(() => {
        if (active) setCatalogError('Failed to load permissions.');
      });
    return () => {
      active = false;
    };
  }, []);

  const required = new Set(
    catalog
      .filter((permission) => data.permissions.includes(permission.key))
      .flatMap((permission) => permission.requires),
  );
  const groups = ['Customers', 'Products', 'Promotions', 'Users', 'Logs', 'System'].filter(
    (group) => catalog.some((permission) => permission.group === group),
  );
  const activeGroup = groups.includes(activeTab) ? activeTab : groups[0];
  const maxLevel = Math.max(
    0,
    ...roles
      .filter((option) => currentUser?.roles.some((item) => item.name === option.name))
      .map((option) => option.level),
  );

  const toggle = (option: PermissionOption, checked: boolean) => {
    const selected = new Set(data.permissions);
    const automatic = new Set(autoPermissions);
    if (checked) {
      selected.add(option.key);
      option.requires.forEach((key) => {
        if (!selected.has(key)) automatic.add(key);
        selected.add(key);
      });
    } else {
      selected.delete(option.key);
      option.requires.forEach((key) => {
        const stillRequired = catalog.some(
          (permission) => selected.has(permission.key) && permission.requires.includes(key),
        );
        if (!stillRequired && automatic.has(key)) {
          selected.delete(key);
          automatic.delete(key);
        }
      });
    }
    setAutoPermissions([...automatic]);
    setData((previous) => ({ ...previous, permissions: [...selected] }));
  };

  return (
    <PageLayout>
      <EntityForm
        title={isEdit ? 'Edit role' : 'Add new role'}
        submitButtonLabel={isEdit ? 'Update role' : 'Add new role'}
        submitButtonFirst
        onCancel={() => {
          void navigate('/users/roles');
        }}
        isEdit={isEdit}
        error={error || catalogError || validation}
        listEndpoint={API_ENDPOINTS.ROLES.LIST}
        requiredPermission={USER_PERMISSIONS.UserRolesEdit}
        submitButtonDisabled={saving || !loaded || (isEdit && !role)}
        onSubmit={(event) => {
          event.preventDefault();
          if (!data.name.trim() || data.permissions.length === 0) {
            setValidation('Title and at least one permission are required.');
            return;
          }
          if (
            data.level.trim() === '' ||
            !Number.isInteger(Number(data.level)) ||
            Number(data.level) < 0 ||
            Number(data.level) > maxLevel
          ) {
            setValidation(`Level must be a whole number between 0 and ${maxLevel}.`);
            return;
          }
          setValidation('');
          setSaving(true);
          void handleSubmit({ ...data, name: data.name.trim(), level: Number(data.level) }).finally(
            () => setSaving(false),
          );
        }}
      >
        <fieldset disabled={saving || !loaded}>
          <CFormInput
            className="mb-3"
            label={
              <>
                Role title <span className="text-danger">*</span>
              </>
            }
            name="name"
            required
            maxLength={255}
            value={data.name}
            onChange={(event) => setData((previous) => ({ ...previous, name: event.target.value }))}
          />
          <CFormInput
            className="mb-3"
            label={
              <>
                Level <span className="text-danger">*</span>
              </>
            }
            name="level"
            type="number"
            required
            min={0}
            max={maxLevel}
            value={data.level}
            onChange={(event) =>
              setData((previous) => ({ ...previous, level: event.target.value }))
            }
          />
          <section className="role-form-permissions" aria-label="Role permissions">
            <div className="d-flex align-items-center justify-content-between gap-3 mb-3">
              <h5 className="mb-0">Permissions</h5>
              <span
                className="text-body-secondary small"
                role="status"
                aria-label="Selected permissions"
              >
                {data.permissions.length} selected
              </span>
            </div>
            <CNav
              variant="tabs"
              role="tablist"
              aria-label="Permission groups"
              className="role-form-permissions__tabs"
            >
              {groups.map((group) => (
                <CNavItem key={group}>
                  <CNavLink
                    as="button"
                    type="button"
                    role="tab"
                    id={`permission-tab-${group}`}
                    aria-label={group}
                    aria-selected={activeGroup === group}
                    aria-controls="permission-panel"
                    active={activeGroup === group}
                    onClick={() => setActiveTab(group)}
                  >
                    {group}
                    <CBadge
                      className="role-form-permissions__count"
                      shape="rounded-pill"
                      aria-hidden="true"
                    >
                      {
                        catalog.filter(
                          (option) =>
                            option.group === group && data.permissions.includes(option.key),
                        ).length
                      }
                    </CBadge>
                  </CNavLink>
                </CNavItem>
              ))}
            </CNav>
            {activeGroup && (
              <div
                className="role-form-permissions__panel"
                role="tabpanel"
                id="permission-panel"
                aria-labelledby={`permission-tab-${activeGroup}`}
              >
                <CTable responsive className="mb-0" aria-label="Permissions">
                  <CTableBody>
                    {catalog
                      .filter((option) => option.group === activeGroup)
                      .sort((left, right) => left.key.localeCompare(right.key))
                      .map((option) => (
                        <CTableRow key={option.key}>
                          <CTableDataCell className="text-center align-middle">
                            <CFormCheck
                              id={option.key}
                              aria-label={option.key}
                              checked={data.permissions.includes(option.key)}
                              disabled={required.has(option.key)}
                              onChange={(event) => toggle(option, event.target.checked)}
                            />
                          </CTableDataCell>
                          <CTableDataCell className="align-middle fw-medium">
                            <label htmlFor={option.key} style={{ cursor: 'pointer' }}>
                              {option.key}
                            </label>
                          </CTableDataCell>
                          <CTableDataCell className="align-middle text-body-secondary">
                            {option.description}
                          </CTableDataCell>
                        </CTableRow>
                      ))}
                  </CTableBody>
                </CTable>
              </div>
            )}
          </section>
        </fieldset>
      </EntityForm>
    </PageLayout>
  );
};
